import { Menu as BaseMenu } from '@base-ui/react/menu'
import type { FC, HTMLAttributes, ReactNode } from 'react'
import { createContext, useContext } from 'react'
import styles from './menu.module.css'

/** Tracks whether we're inside a Menu.Root so MenuItem can self-wrap when standalone */
const MenuContext = createContext(false)

export interface MenuProps {
  open: boolean
  onClose?: () => void
  anchorEl?: HTMLElement | null
  anchorOrigin?: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'right' | 'center' }
  className?: string
  id?: string
  style?: React.CSSProperties
  children?: ReactNode
  /** @deprecated Ignored - kept for MUI compat */
  keepMounted?: boolean
  /** @deprecated Ignored - kept for MUI compat */
  MenuListProps?: Record<string, unknown>
}

export const Menu: FC<MenuProps> = ({
  open,
  onClose,
  anchorEl,
  className,
  children,
  style,
  id,
  keepMounted: _keepMounted,
  MenuListProps,
}) => {
  const classes = [styles.menu, className].filter(Boolean).join(' ')

  return (
    <MenuContext.Provider value={true}>
      <BaseMenu.Root
        open={open}
        onOpenChange={(o, eventDetails) => {
          /**
           * GH #1474: Base UI emits `onOpenChange(false, { reason: 'sibling-open' })`
           * on the parent root when a submenu opens (the submenu is a "sibling" in
           * its floating tree). Forwarding that to `onClose` tore the whole menu
           * down as soon as a user navigated into a submenu. Only propagate genuine
           * dismissals — never the sibling-open transition.
           */
          if (!o && eventDetails.reason !== 'sibling-open') {
            onClose?.()
          }
        }}
      >
        <BaseMenu.Portal>
          <BaseMenu.Positioner anchor={anchorEl ?? undefined} style={{ zIndex: 'var(--tuna-zIndex-modal)' as never }}>
            <BaseMenu.Popup className={classes} style={style} id={id} {...MenuListProps}>
              {children}
            </BaseMenu.Popup>
          </BaseMenu.Positioner>
        </BaseMenu.Portal>
      </BaseMenu.Root>
    </MenuContext.Provider>
  )
}

export interface MenuItemProps extends HTMLAttributes<HTMLDivElement> {
  selected?: boolean
  disabled?: boolean
  dense?: boolean
  value?: string | number
  /** Forwarded to BaseMenu.Item. When false, clicking the item does not close the parent menu. Defaults to true. */
  closeOnClick?: boolean
  ref?: React.Ref<HTMLElement>
}

const menuItemClasses = (className?: string, selected?: boolean, _disabled?: boolean) =>
  [styles.item, selected ? styles.selected : undefined, className].filter(Boolean).join(' ')

/** MenuItem that renders as BaseMenu.Item inside a Menu, or a plain div when standalone */
export const MenuItem: FC<MenuItemProps> = ({
  selected,
  disabled,
  value,
  className,
  onClick,
  onKeyDown,
  dense: _dense,
  ref,
  ...rest
}) => {
  const insideMenu = useContext(MenuContext)

  if (insideMenu) {
    return (
      <BaseMenu.Item
        ref={ref}
        className={menuItemClasses(className, selected, disabled)}
        disabled={disabled}
        data-value={value}
        onClick={disabled ? undefined : onClick}
        {...rest}
      />
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      e.currentTarget.click()
    }
    onKeyDown?.(e)
  }

  return (
    <div
      ref={ref as React.Ref<HTMLDivElement>}
      role="menuitem"
      tabIndex={disabled ? -1 : 0}
      className={menuItemClasses(className, selected, disabled)}
      aria-disabled={disabled || undefined}
      data-value={value}
      onClick={disabled ? undefined : onClick}
      onKeyDown={disabled ? undefined : handleKeyDown}
      {...rest}
    />
  )
}

/**
 * Groups the parts of a flyout submenu. Thin re-export of Base UI's SubmenuRoot,
 * which owns all keyboard nav, ARIA, focus restoration, hover delay, and edge-flipping.
 */
// eslint-disable-next-line react-refresh/only-export-components -- re-export of a Base UI component the rule can't statically resolve
export const Submenu = BaseMenu.SubmenuRoot

export interface SubmenuTriggerProps extends HTMLAttributes<HTMLDivElement> {
  disabled?: boolean
  ref?: React.Ref<HTMLElement>
}

/** A menu row that opens a submenu. Renders Base UI's SubmenuTrigger with the shared item style plus a chevron. */
export const SubmenuTrigger: FC<SubmenuTriggerProps> = ({ className, disabled, ref, ...rest }) => (
  <BaseMenu.SubmenuTrigger
    ref={ref}
    disabled={disabled}
    className={[styles.item, styles.submenuTrigger, className].filter(Boolean).join(' ')}
    {...rest}
  />
)

export interface SubmenuContentProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode
}

/** The popup panel for a submenu. Mirrors the top-level Menu's Portal → Positioner → Popup structure. */
export const SubmenuContent: FC<SubmenuContentProps> = ({ className, children, style, id, ...rest }) => {
  const classes = [styles.menu, className].filter(Boolean).join(' ')

  return (
    <MenuContext.Provider value={true}>
      <BaseMenu.Portal>
        <BaseMenu.Positioner style={{ zIndex: 'var(--tuna-zIndex-modal)' as never }}>
          <BaseMenu.Popup className={classes} style={style} id={id} {...rest}>
            {children}
          </BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </MenuContext.Provider>
  )
}
